import React from 'react'
import { AlertOctagon, RefreshCw, Home } from 'lucide-react'

/**
 * RootErrorBoundary
 * Catches render errors in the route tree so a single broken page can't
 * white-screen the whole app. Forwards to Sentry if available; otherwise
 * logs to the console. Provides a clear recovery path for the user.
 */
export default class RootErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, info) {
    // Best-effort Sentry forward (the SDK may not be loaded).
    try {
      if (window.Sentry?.captureException) {
        window.Sentry.captureException(error, { extra: info })
      }
    } catch (_) {
      /* ignore */
    }
    // eslint-disable-next-line no-console
    console.error('RootErrorBoundary caught:', error, info)
  }

  handleReload = () => {
    this.setState({ error: null })
    window.location.reload()
  }

  handleHome = () => {
    this.setState({ error: null })
    window.location.assign('/')
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
            <AlertOctagon className="w-6 h-6 text-red-600" aria-hidden="true" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-1">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-5">
            We hit an unexpected error rendering this page. The error has been logged. You can reload to try again.
          </p>
          {process.env.NODE_ENV !== 'production' && this.state.error?.message && (
            <pre className="text-xs text-left bg-gray-100 text-gray-700 p-2 rounded mb-4 overflow-x-auto max-h-32">
              {String(this.state.error.message)}
            </pre>
          )}
          <div className="flex gap-2 justify-center">
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" /> Reload page
            </button>
            <button
              onClick={this.handleHome}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              <Home className="w-4 h-4" /> Go home
            </button>
          </div>
        </div>
      </div>
    )
  }
}
